"use client"

import { shopifyImg } from "@/lib/util/img"

import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { HttpTypes } from "@medusajs/types"
import { clx } from "@medusajs/ui"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"

type Cat = HttpTypes.StoreProductCategory

export const sortByRank = (a: Cat, b: Cat) => (a.rank ?? 0) - (b.rank ?? 0)

// Header labels (3 Oct 2026, PDF redesign nav "Shop · Brands · Sell on Elevate"): short so six items fit; Contact shows from 1680px and is in the footer
const SHORT: Record<string, string> = { "shop-online": "Shop", "our-brands": "Brands" }

const MegaMenu = ({ categories, approved = false }: { categories: Cat[]; approved?: boolean }) => {
  const [open, setOpen] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()

  const close = useCallback(() => setOpen(null), [])
  useEffect(() => close(), [pathname, close])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close()
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close()
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onClick)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onClick)
    }
  }, [close])

  const top = categories.filter((c) => !c.parent_category_id).sort(sortByRank)
  const kids = (id: string) => categories.filter((c) => c.parent_category_id === id).sort(sortByRank)
  const enter = (id: string) => {
    if (timer.current) clearTimeout(timer.current)
    setOpen(id)
  }
  const leave = () => {
    timer.current = setTimeout(() => setOpen(null), 180)
  }
  const trigger =
    "flex items-center gap-1 whitespace-nowrap uppercase tracking-[0.06em] text-sm font-medium px-2 large:px-3 py-2 hover:text-elevate-gold-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-elevate-gold"

  // Disclosure navigation inside <nav aria-label="Main"> (nav/index.tsx). No role=menubar: that role requires menuitem
  // children and arrow-key handling, neither of which this has (axe aria-required-children on every page, 25 Sep 2026).
  return (
    <div ref={rootRef} className="flex items-center">
      {top.map((cat) => {
        const children = kids(cat.id)
        const isBrands = cat.handle === "our-brands"
        const panelId = `menu-${cat.handle}`
        const isOpen = open === cat.id
        return (
          <div key={cat.id} onMouseEnter={() => enter(cat.id)} onMouseLeave={leave}>
            <button
              className={clx(trigger, isOpen && "text-elevate-gold-dark")}
              aria-expanded={isOpen}
              aria-controls={panelId}
              aria-haspopup="true"
              onClick={() => setOpen(isOpen ? null : cat.id)}
            >
              {SHORT[cat.handle] ?? cat.name}
              <svg aria-hidden width="8" height="8" viewBox="0 0 10 10" className={clx("transition-transform", isOpen && "rotate-180")}>
                <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </button>
            {isOpen && (
              <div id={panelId} className="absolute left-0 right-0 top-full bg-white border-y border-elevate-border shadow-[0_12px_24px_-12px_rgba(0,0,0,0.15)] z-50">
                <div className="content-container grid grid-cols-12 gap-10 py-8">
                  <div className="col-span-9">
                    <div className="flex items-baseline justify-between mb-5">
                      <p className="text-xs uppercase tracking-[0.15em] text-elevate-gold-dark">{isBrands ? "Shop by brand" : "Shop by category"}</p>
                      <LocalizedClientLink href="/store" className="text-xs underline underline-offset-4 hover:text-elevate-gold-dark">
                        View all products →
                      </LocalizedClientLink>
                    </div>
                    <ul className={clx("grid gap-x-6 gap-y-1", isBrands ? "grid-cols-3" : "grid-cols-3")}>
                      {children.map((c) => {
                        const img = (c.metadata as any)?.image as string | undefined
                        const count = (c as any).product_count ?? c.products?.length ?? 0
                        return (
                          <li key={c.id}>
                            <LocalizedClientLink
                              href={`/categories/${c.handle}`}
                              className="group flex items-center gap-3 rounded-image px-2 py-2 hover:bg-elevate-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-elevate-gold"
                            >
                              {isBrands && (
                                <span className="h-10 w-10 shrink-0 overflow-hidden rounded-image border border-elevate-border bg-elevate-grey-light">
                                  {img && <img src={shopifyImg(img, 120)} alt="" loading="lazy" width={40} height={40} className="h-full w-full object-cover" />}
                                </span>
                              )}
                              <span className="flex flex-col">
                                <span className="font-heading text-base leading-tight group-hover:text-elevate-gold-dark">{c.name}</span>
                                <span className="text-xs text-elevate-grey-mid">{count} products</span>
                              </span>
                            </LocalizedClientLink>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                  <div className="col-span-3">
                    {/* 5 Oct 2026: the catalogue is trade only (emailed to approved accounts on request); no public PDF download. */}
                    <LocalizedClientLink
                      href={approved ? "/contact" : "/account?view=register"}
                      className="group block overflow-hidden rounded-image bg-elevate-black text-white"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={shopifyImg("https://media.elevatewholesale.com.au/files/catalogue-cover-trade-2026-10.jpg", 600)} alt="Elevate Wholesale trade price catalogue" loading="lazy" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-4">
                        <p className="text-xs uppercase tracking-[0.15em] text-elevate-gold">Trade catalogue</p>
                        <p className="font-heading text-xl mt-1">{approved ? "Ask us for the trade catalogue" : "Sign up with your ABN for the trade catalogue"}</p>
                      </div>
                    </LocalizedClientLink>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
      {approved ? (
        <>
          <LocalizedClientLink href="/quick-order" className={trigger}>
            Quick order
          </LocalizedClientLink>
          <LocalizedClientLink href="/specials" className={trigger}>
            Specials
          </LocalizedClientLink>
        </>
      ) : (
        <LocalizedClientLink href="/account?view=register" className={trigger}>
          Trade signup
        </LocalizedClientLink>
      )}
      <LocalizedClientLink href="/brand-partnerships" className={trigger}>
        Partner with us
      </LocalizedClientLink>
      <LocalizedClientLink href="/contact" className={clx(trigger, "hidden xlarge:flex")}>
        Contact
      </LocalizedClientLink>
    </div>
  )
}

export default MegaMenu
